/**
 * Editor для doc.cash_shift_open (Порція Роздріб.5b).
 * Відкриття зміни — простий документ. Backend перевіряє sentinel
 * "no_open_shift_for_workstation" при post.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import { retailCashShiftOpens } from '@/api/retailCashShiftOpen';
import { retailPosWorkstations } from '@/api/retailPosWorkstations';
import { useNumberPreview } from '@/hooks/useNumberPreview';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import RequirePermission from '@/auth/RequirePermission';
import { validateRequired } from '@/utils/validateRequired';
import { extractApiError } from '@/utils/extractApiError';
import RequiredLabel from '@/components/RequiredLabel';
import DatePopover from '@/components/DatePopover';

const PERMISSION = 'retail.shift.open';

interface FormState {
  organization_id: string;
  document_number: string;
  document_date: string;
  pos_workstation_id: string;
  opening_cash_amount: string;
  comment: string;
}

const EMPTY: FormState = {
  organization_id: '',
  document_number: '',
  document_date: '',
  pos_workstation_id: '',
  opening_cash_amount: '0',
  comment: '',
};

export default function CashShiftOpenEditor() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { id } = useParams();
  const isNew = !id || id === 'new';

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const { availableOrgs } = usePermissionsByOrg(PERMISSION);

  const [form, setForm] = useState<FormState>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const wsQ = useQuery({
    queryKey: ['retail-pos-workstations-for-shift-open', form.organization_id],
    queryFn: () => retailPosWorkstations.list({ organization_id: form.organization_id, limit: 500 }),
    enabled: !!form.organization_id,
  });

  const docQuery = useQuery({
    queryKey: ['retail-cash-shift-open', id],
    queryFn: () => retailCashShiftOpens.get(id!),
    enabled: !isNew,
  });

  const numberPreview = useNumberPreview('doc.cash_shift_open', form.organization_id, isNew);

  useEffect(() => {
    if (isNew && availableOrgs.length > 0 && !form.organization_id) {
      setForm((f) => ({ ...f, organization_id: availableOrgs[0]!.id }));
    }
  }, [isNew, availableOrgs, form.organization_id]);

  useEffect(() => {
    if (!isNew && docQuery.data) {
      const d = docQuery.data;
      setForm({
        organization_id: d.organization_id,
        document_number: d.document_number,
        document_date: d.document_date.slice(0, 16),
        pos_workstation_id: d.pos_workstation_id,
        opening_cash_amount: d.opening_cash_amount,
        comment: d.comment ?? '',
      });
    }
  }, [isNew, docQuery.data]);

  const posted = !!docQuery.data?.posted;
  const shiftStatus = docQuery.data?.shift_status;

  const create = useMutation({
    mutationFn: () =>
      retailCashShiftOpens.create({
        organization_id: form.organization_id,
        document_number: form.document_number || null,
        document_date: form.document_date ? new Date(form.document_date).toISOString() : null,
        pos_workstation_id: form.pos_workstation_id,
        opening_cash_amount: form.opening_cash_amount,
        comment: form.comment || null,
      }),
    onSuccess: (doc) => {
      qc.invalidateQueries({ queryKey: ['retail-cash-shift-open'] });
      navigate(`/retail/cash-shift-open/${doc.id}`);
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const update = useMutation({
    mutationFn: () =>
      retailCashShiftOpens.update(id!, {
        document_number: form.document_number || null,
        document_date: form.document_date ? new Date(form.document_date).toISOString() : null,
        opening_cash_amount: form.opening_cash_amount,
        comment: form.comment || null,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-cash-shift-open'] });
      qc.invalidateQueries({ queryKey: ['retail-cash-shift-open', id] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const post = useMutation({
    mutationFn: () => retailCashShiftOpens.post(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-cash-shift-open'] });
      qc.invalidateQueries({ queryKey: ['retail-cash-shift-open', id] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const unpost = useMutation({
    mutationFn: () => retailCashShiftOpens.unpost(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-cash-shift-open'] });
      qc.invalidateQueries({ queryKey: ['retail-cash-shift-open', id] });
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const remove = useMutation({
    mutationFn: () => retailCashShiftOpens.remove(id!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['retail-cash-shift-open'] });
      navigate('/retail/cash-shift-open');
    },
    onError: (e) => setError(extractApiError(e)),
  });

  const handleSave = () => {
    setError(null);
    const err = validateRequired(
      {
        organization_id: form.organization_id,
        pos_workstation_id: form.pos_workstation_id,
      },
      {
        organization_id: 'Юрособа',
        pos_workstation_id: 'POS-станція',
      },
    );
    if (err) { setError(err); return; }
    if (isNew) create.mutate();
    else update.mutate();
  };

  return (
    <div className="card">
      <div className="toolbar">
        <h2 style={{ margin: 0 }}>
          {isNew
            ? `Відкриття зміни ${numberPreview ? `(${numberPreview})` : ''}`
            : `Відкриття зміни ${form.document_number}`}
          {posted && (
            <span style={{ marginLeft: '0.75rem', color: '#15803d', fontSize: '1rem' }}>
              ✓ Проведено ({shiftStatus === 'open' ? 'Відкрита' : shiftStatus})
            </span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => navigate('/retail/cash-shift-open')}>Скасувати</button>
          {!posted && (
            <RequirePermission code={PERMISSION}>
              <button
                className="primary"
                onClick={handleSave}
                disabled={create.isPending || update.isPending}
              >
                Зберегти
              </button>
            </RequirePermission>
          )}
          {!isNew && !posted && (
            <RequirePermission code={PERMISSION}>
              <button className="primary" onClick={() => post.mutate()} disabled={post.isPending}>
                ▶ Відкрити зміну
              </button>
            </RequirePermission>
          )}
          {!isNew && posted && shiftStatus === 'open' && (
            <RequirePermission code={PERMISSION}>
              <button onClick={() => unpost.mutate()} disabled={unpost.isPending}>
                ◀ Скасувати відкриття
              </button>
            </RequirePermission>
          )}
          {!isNew && !posted && (
            <RequirePermission code={PERMISSION}>
              <button
                className="danger"
                onClick={() => {
                  if (window.confirm('Видалити документ?')) remove.mutate();
                }}
              >
                Видалити
              </button>
            </RequirePermission>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
        <div className="field">
          <RequiredLabel>Організація</RequiredLabel>
          {isNew ? (
            <select
              value={form.organization_id}
              onChange={(e) => setForm({ ...form, organization_id: e.target.value })}
            >
              <option value="">—</option>
              {availableOrgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.code} — {o.name}
                </option>
              ))}
            </select>
          ) : (
            <div>
              {orgList.data?.items.find((o) => o.id === form.organization_id)?.name ??
                form.organization_id}
            </div>
          )}
        </div>
        <div className="field">
          <label>Дата відкриття</label>
          <DatePopover
            withTime
            value={form.document_date}
            onChange={(v) => setForm({ ...form, document_date: v })}
            disabled={posted}
          />
        </div>
        <div className="field">
          <RequiredLabel>POS-станція</RequiredLabel>
          <select
            value={form.pos_workstation_id}
            onChange={(e) => setForm({ ...form, pos_workstation_id: e.target.value })}
            disabled={!isNew}
          >
            <option value="">—</option>
            {wsQ.data?.items.map((w) => (
              <option key={w.id} value={w.id}>
                {w.code} — {w.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label>Розмін (грн)</label>
          <input
            type="number"
            step="0.01"
            value={form.opening_cash_amount}
            onChange={(e) => setForm({ ...form, opening_cash_amount: e.target.value })}
            disabled={posted}
          />
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label>Коментар</label>
          <textarea
            value={form.comment}
            onChange={(e) => setForm({ ...form, comment: e.target.value })}
            rows={2}
            disabled={posted}
          />
        </div>
      </div>

      {error && (
        <div className="error" style={{ marginTop: '1rem', color: '#b91c1c' }}>
          {error}
        </div>
      )}
    </div>
  );
}
